import React, { useState, useEffect } from "react";
import Pagination from "react-js-pagination";

/**
 * The PaginatedItemsTable component in JavaScript renders a table with paginated items, allowing
 * selection and pagination functionality.
 * @returns The `PaginatedItemsTable` component is being returned. It displays a table with paginated
 * items based on the `items` array provided as a prop. The table includes a title, item details, and
 * checkboxes for selecting items. Pagination controls are shown at the bottom if there are enough
 * items to paginate.
 */
const PaginatedItemsTable = ({
  items,
  selectedItems,
  onItemSelect,
  itemType,
  itemsPerPage,
  tableTitle,
  searchQuery,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, items.length]);

  // Calculate total items
  const totalItemsCount = items.length;

  // Get current page data
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalItemsCount, itemsPerPage]);

  return (
    <div>
      <div
        className="table-responsive"
        style={{ maxHeight: "56vh", overflow: "auto" }}
      >
        <table
          className="table table-hover table-wrap"
          style={{ margin: "20px 0" }}
        >
          <thead>
            <tr>
              <th>{tableTitle}</th>
              {itemType === "device" && <th>Device S/N</th>}
              <th style={{ width: "50px" }}>Select</th>
            </tr>
          </thead>
          <tbody>
            {getCurrentPageItems().map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                {itemType === "device" && <td>{item.serialNo}</td>}
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => onItemSelect(item.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalItemsCount > 0 && (
        <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
          Showing {getCurrentPageItems().length} of {totalItemsCount} results
          {totalItemsCount > itemsPerPage && ` (Page ${currentPage} of ${Math.ceil(totalItemsCount / itemsPerPage)})`}
        </div>
      )}

      {/* Pagination - Only show if there are enough items */}
      {totalItemsCount > itemsPerPage && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
            paddingRight: "10px",
          }}
        >
          <Pagination
            activePage={currentPage}
            itemsCountPerPage={itemsPerPage}
            totalItemsCount={totalItemsCount}
            pageRangeDisplayed={3}
            onChange={handlePageChange}
            itemClass="page-item"
            linkClass="page-link"
            innerClass="pagination pagination-sm mb-0"
          />
        </div>
      )}
    </div>
  );
};

export default PaginatedItemsTable;
